import { Component } from 'preact';
import { createPortal } from 'preact/compat';
import { UI, playUI } from '../utils/arena/uiSounds';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  BEVELED_BTN, GOLD_BTN, DIALOG_BG, DIALOG_BORDER, FourCorners,
} from '../lib/medievalTheme';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { isPhoneLayout } from '../utils/platform';
import { ARROW_LENGTH, computeTutorialPlacement, readOverlayBounds } from './tutorialPlacement';
import { t } from '../localization/t';

// Session-constant (see platform.js). On phone the modal card becomes a
// full-width sheet (bottom-anchored, or top when the spotlight target is
// in the lower half), typography is restated in rem, and the overlay is
// portalled INTO .app-canvas--phone: portalling to document.body puts it
// outside the canvas, where the cqw design tokens resolve against the
// raw viewport (~8px/cqw) and everything renders microscopic.
const PHONE = isPhoneLayout();

/**
 * TutorialOverlay. Medieval-styled step-by-step onboarding, shared
 * across every screen that wants a guided walkthrough (main menu,
 * game board, deck builder, etc.).
 *
 * How it works:
 *   - The parent passes a list of `steps`, each { key, title, body,
 *     selector?, getRect? }. `selector` is a CSS selector resolved
 *     at measurement time; `getRect` is an escape hatch for targets
 *     that aren't in the DOM (e.g. 3D-canvas elements whose screen
 *     position needs camera projection). If neither is supplied the
 *     step renders as a centered explanatory modal with no arrow.
 *   - On every step change (or window resize), we read the target's
 *     bounding rect via the selector or the callback and compute
 *     two things: the spotlight cutout rectangle and the modal +
 *     arrow position.
 *   - The backdrop is a full-viewport dimmer with an SVG mask cutout
 *     around the target so the highlighted element stays bright
 *     while everything else darkens.
 *   - An animated gold arrow points at the target from the modal
 *     side (auto-chosen: the side of the target away from the canvas
 *     centre, or above/below when neither side has room).
 *   - The modal card holds title, body, step counter, and
 *     Back/Skip/Continue buttons. On the final step, Continue becomes
 *     "Got it." Skip always dismisses the whole tutorial.
 *   - `mandatory` removes every self-serve exit (no Skip button, no
 *     ESC). Pair it with a requireAction step; the parent detects the
 *     taught action itself and unmounts the overlay once the player
 *     has actually performed it.
 *
 * The overlay is position:fixed so it works regardless of what
 * wraps it. The spotlight, arrow, and modal all live in pure
 * screen space, but they are laid out against the `.app-canvas`
 * rect rather than the window, so the letterbox bars on a wide or
 * short viewport stay empty. See tutorialPlacement.js.
 */

const BACKDROP_ALPHA = 0.62;
const SPOTLIGHT_RADIUS = 14;

export default class TutorialOverlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stepIndex: 0,
      targetRect: null,
      measuredModalHeight: null,
    };
    this._raf = null;
    this._modalRef = null;
  }

  setModalRef = (el) => {
    this._modalRef = el;
    this.measureModalHeight();
  };

  measureModalHeight = () => {
    if (!this._modalRef) return;
    const h = this._modalRef.getBoundingClientRect().height;
    if (!Number.isFinite(h) || h <= 0) return;
    if (this.state.measuredModalHeight !== h) {
      this.setState({ measuredModalHeight: h });
    }
  };

  componentDidMount() {
    this.emitStepChange();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleKeyDown);
    // Layout below the spotlight can shift after mount: late-loading
    // images (e.g. the hub logo) flow in and push the target down. A
    // ResizeObserver on body catches that, and a `load` listener in
    // the capture phase catches image-load events from anywhere in
    // the document.
    if (typeof ResizeObserver !== 'undefined') {
      this._bodyObserver = new ResizeObserver(() => this.queueMeasure());
      this._bodyObserver.observe(document.body);
    }
    window.addEventListener('load', this.handleLoad, true);
    if (!this.props.mandatory) this._escDismissId = pushDismiss(() => this.skip());
    this.runStepOnEnter();
    this.queueMeasure();
    this.scheduleSettleRemeasures();
    this.syncRectTracking();
    playUI(UI.OPEN);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.stepIndex !== this.state.stepIndex) {
      this.setState({ measuredModalHeight: null });
      this.runStepOnEnter();
      this.emitStepChange();
      this.queueMeasure();
      this.scheduleSettleRemeasures();
      this.syncRectTracking();
    }
    this.measureModalHeight();
  }

  // Steps targeting elements that move continuously (3D-canvas targets
  // that ride the camera) set `trackRect: true` to re-measure every
  // frame instead of only at resize/settle. measureTarget's
  // change-detection keeps the loop render-free while nothing moves.
  syncRectTracking() {
    this.stopRectTracking();
    if (!this.currentStep()?.trackRect) return;
    const tick = () => {
      this.measureTarget();
      this._trackRaf = requestAnimationFrame(tick);
    };
    this._trackRaf = requestAnimationFrame(tick);
  }

  stopRectTracking() {
    if (this._trackRaf) cancelAnimationFrame(this._trackRaf);
    this._trackRaf = null;
  }

  emitStepChange() {
    const step = this.currentStep();
    if (step) this.props.onStepChange?.(step.key);
  }

  runStepOnEnter() {
    const step = this.currentStep();
    if (step && typeof step.onEnter === 'function') {
      try { step.onEnter(); } catch {}
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('load', this.handleLoad, true);
    if (this._escDismissId != null) removeDismiss(this._escDismissId);
    if (this._bodyObserver) this._bodyObserver.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    this.stopRectTracking();
    this.clearSettleTimers();
  }

  scheduleSettleRemeasures() {
    this.clearSettleTimers();
    this._settleTimers = [150, 500, 1200, 2500].map((ms) =>
      setTimeout(() => this.queueMeasure(), ms),
    );
  }

  clearSettleTimers() {
    if (this._settleTimers) {
      for (const t of this._settleTimers) clearTimeout(t);
    }
    this._settleTimers = null;
  }

  handleLoad = () => this.queueMeasure();

  handleResize = () => this.queueMeasure();

  handleKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'Enter') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.back();
    }
  };

  queueMeasure() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.measureTarget();
    });
  }

  measureTarget() {
    const step = this.currentStep();
    if (!step) return;

    // Two target sources: a CSS selector for DOM-based targets, or
    // a getRect callback for 3D-canvas / computed targets. If the
    // step supplies neither (or both return nothing) we centre the
    // modal as a no-target explanatory step instead of soft-locking.
    let rect = null;
    if (typeof step.getRect === 'function') {
      try {
        rect = step.getRect() || null;
      } catch {
        rect = null;
      }
    }
    if (!rect && step.selector) {
      const el = document.querySelector(step.selector);
      if (el) rect = el.getBoundingClientRect();
    }
    const prev = this.state.targetRect;
    if (!rect) {
      if (prev) this.setState({ targetRect: null });
      return;
    }
    // Skip the setState when nothing moved (sub-pixel jitter included)
    // so the trackRect per-frame loop doesn't re-render a still scene.
    if (
      prev
      && Math.abs(prev.left - rect.left) < 0.5
      && Math.abs(prev.top - rect.top) < 0.5
      && Math.abs(prev.width - rect.width) < 0.5
      && Math.abs(prev.height - rect.height) < 0.5
    ) {
      return;
    }
    this.setState({
      targetRect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      },
    });
  }

  currentStep() {
    const { steps } = this.props;
    if (!Array.isArray(steps) || steps.length === 0) return null;
    const idx = Math.min(Math.max(this.state.stepIndex, 0), steps.length - 1);
    return steps[idx];
  }

  next = () => {
    const { steps } = this.props;
    const step = this.currentStep();
    if (step?.requireAction && step?.canAdvance === false) return;
    if (this.state.stepIndex >= steps.length - 1) {
      this.finish();
      return;
    }
    playUI(UI.CONFIRM);
    this.setState((s) => ({ stepIndex: s.stepIndex + 1 }));
  };

  back = () => {
    if (this.state.stepIndex === 0) return;
    playUI(UI.CANCEL);
    this.setState((s) => ({ stepIndex: s.stepIndex - 1 }));
  };

  skip = () => {
    playUI(UI.CANCEL);
    this.props.onDismiss?.({ reason: 'skip' });
  };

  finish = () => {
    playUI(UI.CONFIRM);
    this.props.onDismiss?.({ reason: 'finish' });
  };

  render() {
    const { steps } = this.props;
    if (!Array.isArray(steps) || steps.length === 0) return null;

    const { stepIndex, targetRect, measuredModalHeight } = this.state;
    const step = this.currentStep();
    if (!step) return null;

    const isLast = stepIndex === steps.length - 1;
    const isFirst = stepIndex === 0;

    // Everything the overlay points at lives inside `.app-canvas`, so
    // the layout box is the canvas rect, not the window.
    const bounds = readOverlayBounds();

    // Steps can override width/height for content that needs more room
    // (e.g. the keyboard shortcuts grid). Defaults sized for a short
    // paragraph + two buttons.
    const modalWidth = step.width || 400;
    // Cap height to the canvas minus a 16px margin top/bottom so even a
    // shortcut grid with many rows can't escape the frame. Inner body
    // scrolls if content exceeds the cap.
    const maxModalHeight = Math.max(120, bounds.height - 32);
    const fallbackHeight = step.height || 240;
    // Prefer the actual rendered height (captured via the modal ref
    // after mount/update). That prevents the original "clamp assumes
    // 240px, modal is 600px" bug that pushed tall modals off-screen.
    const modalHeight = Math.min(
      measuredModalHeight || fallbackHeight,
      maxModalHeight,
    );

    const placement = computeTutorialPlacement({
      bounds,
      targetRect,
      modalWidth,
      modalHeight,
    });
    const { spotlight, modalLeft, modalTop } = placement;

    // Phone sheet: full width, anchored to the screen edge away from
    // the spotlight so the highlighted element stays visible. All the
    // desktop side-anchoring/arrow math is skipped.
    let phoneSheetStyle = null;
    if (PHONE) {
      const targetInLowerHalf = spotlight
        ? spotlight.y + spotlight.height / 2 > bounds.top + bounds.height / 2
        : false;
      phoneSheetStyle = {
        left: '4%',
        right: '4%',
        margin: '0 auto',
        maxWidth: '40rem',
        maxHeight: `${Math.round(bounds.height * 0.6)}px`,
        ...(targetInLowerHalf
          ? { top: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }
          : { bottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }),
      };
    }

    // Arrow origin sits between the modal and the target, pointing
    // from the modal side of the spotlight toward the target centre.
    const arrowStyle = PHONE ? null : placement.arrow;

    // The overlay is portalled directly into document.body so it
    // never inherits an ancestor's `transform`, `filter`, or other
    // containing-block properties. Its `position: fixed` resolves
    // against the viewport regardless of where it's mounted in the
    // tree.
    const body = (
      <div
        aria-modal="true"
        role="dialog"
        className="fixed inset-0 z-[1500] pointer-events-none"
        style={{ animation: 'tutorialFadeIn 180ms ease-out' }}
      >
        {/* Dimmed backdrop with a spotlight cutout. SVG mask is
            cheap, GPU-composited, and lets us apply the dim as a
            single rect so the whole thing is a single layer. */}
        <svg
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
          style={{ pointerEvents: 'none' }}
        >
          <defs>
            <mask id="tutorial-spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlight && (
                <rect
                  x={spotlight.x}
                  y={spotlight.y}
                  width={spotlight.width}
                  height={spotlight.height}
                  rx={SPOTLIGHT_RADIUS}
                  ry={SPOTLIGHT_RADIUS}
                  fill="black"
                  style={{ transition: 'x 260ms ease, y 260ms ease, width 260ms ease, height 260ms ease' }}
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={`rgba(4, 3, 2, ${BACKDROP_ALPHA})`}
            mask="url(#tutorial-spotlight-mask)"
          />
          {/* Gold ring around the spotlight. Draws on top of the
              dimmed backdrop via a second rect with a stroke only. */}
          {spotlight && (
            <rect
              x={spotlight.x}
              y={spotlight.y}
              width={spotlight.width}
              height={spotlight.height}
              rx={SPOTLIGHT_RADIUS}
              ry={SPOTLIGHT_RADIUS}
              fill="none"
              stroke="rgba(212, 168, 67, 0.95)"
              strokeWidth="2"
              style={{
                filter: 'drop-shadow(0 0 16px rgba(212,168,67,0.6))',
                transition: 'x 260ms ease, y 260ms ease, width 260ms ease, height 260ms ease',
              }}
            />
          )}
        </svg>

        {/* Animated arrow pointing at the target from the modal. */}
        {arrowStyle && (
          <div
            className="absolute"
            style={{
              left: arrowStyle.left,
              top: arrowStyle.top,
              width: ARROW_LENGTH,
              height: '2.25cqw',
              pointerEvents: 'none',
              animation: 'tutorialArrowNudge 1.3s ease-in-out infinite',
              // The nudge keyframes re-declare `transform`, so the
              // rotation has to travel with them as a variable or the
              // arrow snaps back to pointing right mid-animation.
              '--r': `${arrowStyle.rotate}deg`,
              transform: `rotate(${arrowStyle.rotate}deg)`,
              transformOrigin: 'center',
              transition: 'left 260ms ease, top 260ms ease',
            }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 72 36" width="100%" height="100%">
              <defs>
                <linearGradient id="tutorial-arrow-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(212,168,67,0.25)" />
                  <stop offset="60%" stopColor="rgba(212,168,67,0.95)" />
                  <stop offset="100%" stopColor="#f0d890" />
                </linearGradient>
              </defs>
              <path
                d="M4 18 L52 18 M42 6 L58 18 L42 30"
                fill="none"
                stroke="url(#tutorial-arrow-grad)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ filter: 'drop-shadow(0 0 10px rgba(212,168,67,0.55))' }}
              />
            </svg>
          </div>
        )}

        {/* The medieval modal card. */}
        <div
          ref={this.setModalRef}
          className="absolute flex flex-col"
          style={{
            ...(PHONE ? phoneSheetStyle : {
              left: modalLeft,
              top: modalTop,
              width: modalWidth,
              maxHeight: maxModalHeight,
            }),
            background: DIALOG_BG,
            backgroundColor: '#0e0a06',
            border: `1px solid ${DIALOG_BORDER}`,
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 32px rgba(180,140,60,0.08)',
            padding: PHONE ? '0.875rem 1rem' : '20px 22px 18px',
            color: TEXT_BODY,
            isolation: 'isolate',
            transition: 'left 260ms ease, top 260ms ease',
            animation: 'tutorialModalIn 260ms ease-out',
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />

          {/* Step counter + close */}
          <div className="flex items-center justify-between mb-2">
            <span
              className={PHONE ? 'text-[0.75rem] font-semibold' : 'text-[0.625cqw] font-semibold'}
              style={{ color: `${GOLD} 0.5)` }}
            >
              {t('Tutorial · {step} / {total}', { step: stepIndex + 1, total: steps.length })}
            </span>
            {!this.props.mandatory && (
              <button
                type="button"
                className={PHONE ? 'text-[0.875rem] px-2 py-1 cursor-pointer transition-colors' : 'text-[0.625cqw] cursor-pointer transition-colors'}
                style={{ color: TEXT_MUTED }}
                onMouseEnter={(e) => { e.currentTarget.style.color = TEXT_BODY; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_MUTED; }}
                onClick={this.skip}
              >
                {t('Skip')}
              </button>
            )}
          </div>

          {/* Title */}
          <h2
            className="arena-heading text-lg font-bold leading-tight mb-2"
            style={{
              color: TEXT_PRIMARY,
              textShadow: '0 2px 6px rgba(0,0,0,0.55), 0 0 18px rgba(180,140,60,0.18)',
            }}
          >
            {t(step.title)}
          </h2>

          {/* Body. Scrolls if content exceeds the viewport-capped
              modal height. */}
          <div
            className={PHONE
              ? 'text-[0.9375rem] leading-normal mb-4 min-h-0 flex-1 overflow-y-auto'
              : 'text-[0.78125cqw] leading-snug mb-4 min-h-0 flex-1 overflow-y-auto'}
            style={{ color: TEXT_BODY }}
          >
            {typeof step.body === 'string' ? t(step.body) : step.body}
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4" aria-hidden="true">
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === stepIndex ? 18 : 6,
                  height: PHONE ? '0.25rem' : '0.375cqw',
                  borderRadius: 'var(--radius-full)',
                  background: i === stepIndex ? ACCENT_GOLD : `${GOLD} 0.25)`,
                  transition: 'width 180ms ease, background 180ms ease',
                }}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="flex items-center gap-2 justify-end">
            {!isFirst && (
              <button
                type="button"
                className={PHONE
                  ? 'px-4 text-[0.875rem] font-semibold cursor-pointer transition-all active:scale-[0.98]'
                  : 'px-4 py-1.5 text-[0.6875cqw] font-semibold cursor-pointer transition-all hover:scale-[1.03] active:scale-[0.98]'}
                style={{ ...BEVELED_BTN, backgroundColor: '#0e0a06', color: TEXT_BODY, ...(PHONE ? { minHeight: '2.5rem' } : {}) }}
                onClick={this.back}
              >
                {t('Back')}
              </button>
            )}
            {(() => {
              const gated = step.requireAction && step.canAdvance === false;
              const label = gated
                ? (step.actionLabel ? t(step.actionLabel) : t('Try it'))
                : (isLast ? t('Got it') : t('Continue'));
              const style = gated
                ? { ...GOLD_BTN, opacity: 0.45, filter: 'grayscale(0.6)', cursor: 'not-allowed' }
                : GOLD_BTN;
              return (
                <button
                  type="button"
                  disabled={gated}
                  aria-disabled={gated}
                  className={PHONE
                    ? `px-5 text-[0.875rem] font-bold transition-all ${gated ? '' : 'cursor-pointer active:scale-[0.98]'}`
                    : `px-5 py-1.5 text-[0.75cqw] font-bold transition-all ${gated ? '' : 'cursor-pointer hover:scale-[1.03] active:scale-[0.98]'}`}
                  style={PHONE ? { ...style, minHeight: '2.5rem' } : style}
                  onClick={this.next}
                >
                  {label}
                </button>
              );
            })()}
          </div>
        </div>

        <style>{`
          @keyframes tutorialFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes tutorialModalIn {
            from { opacity: 0; transform: translateY(6px) scale(0.985); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes tutorialArrowNudge {
            0%, 100% { transform: rotate(var(--r, 0deg)) translateX(0); }
            50% { transform: rotate(var(--r, 0deg)) translateX(6px); }
          }
        `}</style>
      </div>
    );

    if (typeof document === 'undefined') return body;
    // Phone: portal into the canvas so the rem-remapped design tokens
    // apply. Desktop keeps document.body (the canvas is transform-scaled
    // there, and the overlay math runs in raw screen space).
    const host = (PHONE && document.querySelector('.app-canvas--phone')) || document.body;
    return createPortal(body, host);
  }
}
